/* eslint-disable react-refresh/only-export-components */
import { type AppAbility, ability } from "@/casl/ability"
import { createContextualCan } from "@casl/react"
import type React from "react"
import { createContext, useContext, useEffect, useState } from "react"

// type RoleName =
//   "Security"
//   | "operasyon görevlisi"
//   | "operasyon müdürü"
//   | "hizmet verilen proje yetkilisi"
//   | "insan kaynakları"
//   | "satın alma"
//   | "pden"
//   | "satış pazarlama"
//   | "lojistik"
//   | "SuperAdmin"
//   | "CustomerAdmin";

export type SubjectName =
  | "Kullanıcılar"
  | "Tüm Kullanıcılar"
  | "Birimler"
  | "Projeler"
  | "Vardiyalar"
  | "Devriye Planları"
  | "Devriye Rotaları"
  | "Tamamlanan Devriyeler"
  | "Profil"
  | "Olumsuzluk Bildirimi"
  | "Talep"
  | "Bildirimler-Devriye"
  | "Bildirimler"
  | "Talimatlar"
  | "Bildirimler-Güvenlik-Görevlisine-Bildirim-Gönder"
  | "Bildirimler-Opersyon-ve-Denetim"
  | "Rol"
  | "Dokümanlar"
  | "Kullanıcı Dokümanları"
  | "Dokümanlar-Operasyon"
  | "Dokümanlar-Lojistik"
  | "Dokümanlar-Personel-ve-İK"
  | "Dokümanlar-Satın-Alma"
  | "Dokümanlar-Proje"
  | "Dokümanlar-5188-Sorumlusu"
  | "Dokümanlar-Satış-Pazarlama-ve-Reklam"
  | "Dokümanlar-Para-ve-Değerli-Eşya-Nakli"
  | "Başkasının Profil Fotoğrafını Değiştir"
  | "Dokümanlar-Maliye"
  | "Dokümanlar-Eğitim"
  | "Dokümanlar-Bilgi-Teknolojileri-ve-Güvenlik-Mühendisliği"
  | "Dokümanlar-Kalite-Yönetimi"
  | "Dokümanlar-Kurumsal-İletişim-ve-Basın"
  | "Dokümanlar-Genel"
  | "Dokümanlar-Denetleme-Raporları"
  | "Devriye Raporu"
  | "Puantaj"
  | "Görevler"
  | "Giriş Geçmişi"
  | "Vardiya Raporları"
  | "Ücret Pusulası";

export type Action =
  | 'manage'
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'start'
  | 'end'
  | 'upload'
  | 'download'
  | 'select'
  | 'notify';

interface Subject {
  name: string
  id: number
}

interface Permission {
  action: string
  subject: Subject
  conditions?: any
}

interface AbilityContextValue {
  ability: AppAbility
  updatePermissions: (permissions: Permission[]) => void
}

const AbilityContext = createContext<AbilityContextValue>({
  ability,
  updatePermissions: () => { },
})

export const AbilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [internalAbility] = useState(() => ability)

  const updatePermissions = (permissions: Permission[]) => {
    const rules = permissions.map(p => ({
      action: p.action,
      subject: p.subject.name,
      conditions: typeof p.conditions === "string" ? JSON.parse(p.conditions) : p.conditions,
    }))

    // Global ability'yi de güncelle
    internalAbility.update(rules)
  }

  // Global ability'yi context ability ile senkronize et
  useEffect(() => {
    const handleAbilityUpdate = () => {
      // Ability güncellendiğinde gerekli işlemler
    }

    internalAbility.on("updated", handleAbilityUpdate)

    return () => {
      (internalAbility as any)?.off("updated", handleAbilityUpdate)
    }
  }, [internalAbility])

  return (
    <AbilityContext.Provider value={{ ability: internalAbility, updatePermissions }}>
      {children}
    </AbilityContext.Provider>
  )
}

export const useAbility = () => useContext(AbilityContext)

export const CanInternal = createContextualCan(AbilityContext.Consumer as any)

interface CanProps {
  children: React.ReactNode
  I: Action
  a: SubjectName
  [key: string]: any
}

export const Can: React.FC<CanProps> = ({ children, I, a, ...rest }) => {
  const { ability } = useAbility()
  return (
    <CanInternal I={I} a={a} ability={ability} {...rest}>
      {children}
    </CanInternal>
  )
}
